import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export type CreateAssessmentParams = {
  assessmentTypeCode: string;
  assessmentName: string;
  managementNumber1?: string;
  managementNumber2?: string;
  managementNumber3?: string;
  userId: string;
  managerId: string;
  information1?: string;
  information2?: string;
  information3?: string;
  note?: string;
};

export type UpdateAssessmentParams = {
  assessmentId: string;
  assessmentName: string;
  managementNumber1?: string;
  managementNumber2?: string;
  managementNumber3?: string;
  userId: string;
  managerId: string;
  information1?: string;
  information2?: string;
  information3?: string;
  note?: string;
};

export type DeleteAssessmentParams = {
  assessmentId: string;
  disposalReasonCode: string;
  disposalDetail?: string;
  disposedAt: string;
};

export type ListAssessmentParams = {
  assessmentTypeCode?: string;
  assessmentName?: string;
  managementNumber1?: string;
  managementNumber2?: string;
  managementNumber3?: string;
  userId?: string;
  managerId?: string;
  combinedInformation?: string;
  includeDisposed?: boolean;
  disposedAtFrom?: string;
  disposedAtTo?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
};

export type AssessmentDetailResult = {
  assessmentId: string;
  assessmentTypeCode: string;
  assessmentTypeName: string;
  assessmentName: string;
  managementNumber1?: string;
  managementNumber2?: string;
  managementNumber3?: string;
  userId: string;
  userName: string;
  managerId: string;
  managerName: string;
  information1?: string;
  information2?: string;
  information3?: string;
  note?: string;
  isDeleted: boolean;
  disposalReasonCode?: string;
  disposalReasonName?: string;
  disposalDetail?: string;
  disposedAt?: string;
  disposalFinancialYear?: number;
  disposalMonth?: number;
  createdAt: string;
  createdFinancialYear: number;
  createdMonth: number;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
};

export type AssessmentListResult = {
  assessmentId: string;
  assessmentTypeCode: string;
  assessmentTypeName: string;
  assessmentName: string;
  managementNumber1?: string;
  managementNumber2?: string;
  managementNumber3?: string;
  userId: string;
  userName: string;
  managerId: string;
  managerName: string;
  isDeleted: boolean;
  disposalReasonCode?: string;
  disposalReasonName?: string;
  disposedAt?: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
};

// --- API関数 ---

/**
 * 資産登録API
 * @param params.assessmentTypeCode - 資産区分コード（必須, 最大300文字, master_type.type_code）
 * @param params.assessmentName - 資産名（必須, 最大200文字）
 * @param params.managementNumber1 - 管理番号1（任意, 最大50文字）
 * @param params.managementNumber2 - 管理番号2（任意, 最大50文字）
 * @param params.managementNumber3 - 管理番号3（任意, 最大50文字）
 * @param params.userId - 利用者ID（必須, 最大300文字, user.user_id）
 * @param params.managerId - 管理者ID（必須, 最大300文字, user.user_id）
 * @param params.information1 - 情報1（任意, 最大100文字）
 * @param params.information2 - 情報2（任意, 最大100文字）
 * @param params.information3 - 情報3（任意, 最大100文字）
 * @param params.note - 備考（任意, 最大10000文字）
 */
export const createAssessment = async (params: CreateAssessmentParams): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_ASSESSMENT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.assessmentId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 資産更新API
 * @param params.assessmentId - 資産ID（必須, 最大300文字）
 * @param params.assessmentName - 資産名（必須, 最大200文字）
 * @param params.managementNumber1 - 管理番号1（任意, 最大50文字）
 * @param params.managementNumber2 - 管理番号2（任意, 最大50文字）
 * @param params.managementNumber3 - 管理番号3（任意, 最大50文字）
 * @param params.userId - 利用者ID（必須, 最大300文字, user.user_id）
 * @param params.managerId - 管理者ID（必須, 最大300文字, user.user_id）
 * @param params.information1 - 情報1（任意, 最大100文字）
 * @param params.information2 - 情報2（任意, 最大100文字）
 * @param params.information3 - 情報3（任意, 最大100文字）
 * @param params.note - 備考（任意, 最大10000文字）
 */
export const updateAssessment = async (params: UpdateAssessmentParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_ASSESSMENT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 資産削除API（除却）
 * @param params.assessmentId - 資産ID（必須, 最大300文字）
 * @param params.disposalReasonCode - 除却理由コード（必須, 最大300文字, master_type.type_code）
 * @param params.disposalDetail - 除却詳細（任意, 最大300文字）
 * @param params.disposedAt - 除却日（必須, format: yyyy-MM-dd'T'HH:mm:ss）
 */
export const deleteAssessment = async (params: DeleteAssessmentParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.DELETE_ASSESSMENT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 資産取得API
 * @param assessmentId - 資産ID（必須, パスパラメータ）
 */
export const getAssessment = async (assessmentId: string): Promise<AssessmentDetailResult> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.get(`${KanzhongBackend.GET_ASSESSMENT}/${assessmentId}`, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 資産一覧取得API
 * @param params.assessmentTypeCode - 資産区分コード（任意, 完全一致）
 * @param params.assessmentName - 資産名（任意, 部分一致）
 * @param params.managementNumber1 - 管理番号1（任意, 完全一致）
 * @param params.managementNumber2 - 管理番号2（任意, 完全一致）
 * @param params.managementNumber3 - 管理番号3（任意, 完全一致）
 * @param params.userId - 利用者ID（任意, 完全一致）
 * @param params.managerId - 管理者ID（任意, 完全一致）
 * @param params.combinedInformation - 統合後情報（任意, 部分一致）
 * @param params.includeDisposed - 除却済みを含む（任意, デフォルトfalse）
 * @param params.disposedAtFrom - 除却日From（任意, 以上, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.disposedAtTo - 除却日To（任意, 以下, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.createdAtFrom - 作成日From（任意, 以上, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.createdAtTo - 作成日To（任意, 以下, format: yyyy-MM-dd'T'HH:mm:ss）
 */
export const listAssessment = async (
  params: ListAssessmentParams,
): Promise<AssessmentListResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_ASSESSMENT, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
